import { createContext, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  GetMe,
  Login,
  Logout,
  selectFactoriesForLogin,
  selectIsAuthentificated,
  selectSessionUser,
  selectSessionIsLoading,
} from '../redux/reducers/SessionReducer';
import { useAppDispatch } from '../redux/store';

//---------------------------------------------------

interface AuthContextType {
  // eslint-disable-next-line no-unused-vars
  onLogin: (data: any, callback: any) => void;
  // eslint-disable-next-line no-unused-vars
  onLogout: (callback: any) => void;
  // eslint-disable-next-line no-unused-vars
  onGetMe: (callback?: () => void) => void;
  account: any;
  role: string | null;
  email: string | null;
  factory: any;
  factories: any[];
  factoriesForLogin: any[];
  isAuthentificated: boolean | null;
  loading: boolean;
  isLoading: boolean;
  localStoragePrefix: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode[] | React.ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const dispatch = useAppDispatch();

  const isAuthentificated = useSelector(selectIsAuthentificated);
  // const [isAuthentificatedCookie] = useCookies(['isAuthentificated']);

  const account = useSelector(selectSessionUser);
  const factoriesForLogin = useSelector(selectFactoriesForLogin);

  const isLoading = useSelector(selectSessionIsLoading);

  // Auth data localStorage state
  // const [token, setToken, removeToken] = useLocalStorage('token', null, true);

  // // Remove all Auth data from localStorage
  // const clearLocalStorage = () => removeToken();

  const value = useMemo(() => {
    // ME
    const handleGetMe = (callback?: () => void) => {
      dispatch(GetMe()).then(() => {
        if (typeof callback === 'function') {
          callback();
        }
      });
    };

    // LOGIN
    const handleLogin = (data, callback) => {
      dispatch(Login(data)).then((res: any) => {
        if (!res.error) {
          const hasMultipleFactories = res.payload.data?.factories?.length > 1;

          if (!hasMultipleFactories) {
            handleGetMe();
            if (typeof callback === 'function') {
              callback();
            }
          }
        }
      });
    };

    // LOGOUT
    const handleLogout = (callback) => {
      dispatch(Logout()).then(() => {
        if (typeof callback === 'function') {
          callback();
        }
      });
    };

    return {
      onLogin: handleLogin,
      onLogout: handleLogout,
      onGetMe: handleGetMe,

      account,
      role: account?.role || null,
      email: account?.email || null,
      factory: account?.factory || null,
      factories: account?.factories || [],

      factoriesForLogin,

      isAuthentificated,
      loading: isAuthentificated === null,

      isLoading,

      localStoragePrefix: `${account.factory?._id}_${account.email?.toUpperCase()}`,
    };
  }, [account, dispatch, factoriesForLogin, isAuthentificated, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthProvider;
